// @givmo/social — the composer as a renter's card (SMM-WEB5): Jonathan's approved walkthrough frame
// 40, the appeal's Social tab with "New post" beside the appeal's scheduled list.
//
// The SAME composer as the page — `useComposer` (`composer-page.tsx`) holds its state, validation,
// requests and refusals — laid out in frame 40's order, top to bottom:
//   * Graphic: the campaign's Collateral graphics first, as tiles, with a tile to add an image; the
//     chosen one outlined, with its remove;
//   * Caption, with its count;
//   * the campaign's link as a fixed line (no link field here), and Instagram's bio hint while an
//     Instagram account is ticked (its captions' links aren't clickable);
//   * Post to: a checkbox per connected account, named by the platform where the organization has
//     one of each and by the account too where it has more; the reconnect line;
//   * a "Preview" disclosure, collapsed, over the same per-account previews and edits;
//   * the date and time inline, and Schedule, the card's act, with the chosen time and zone said
//     right above it, where the person clicks (on the renter's organization's clock where it gives
//     one, `SocialConfig.timeZone`); Publish now, Save draft, the renter's Cancel and Delete draft
//     beside the reason, below.

import { useRef, useState } from 'react';
import { ChevronDown, ChevronRight, ImagePlus, Link2, X } from 'lucide-react';
import {
  Button,
  Card,
  Checkbox,
  ErrorMessage,
  Field,
  Icon,
  Textarea,
  cn,
  testIdProp,
} from '@givmo/design-system';
import type { Channel } from './composer-model';
import {
  LapsedChannels,
  NoChannels,
  PreviewCards,
  UntickDialog,
  useComposer,
  useScheduleFields,
  type ComposerController,
  type ComposerProps,
} from './composer-page';
import { PlatformMark } from './platform-mark';
import { actionErrorWords } from './post-parts';
import { DeleteDraftDialog } from './posts-page';
import { canManageConnections } from './social-config';
import {
  IMAGE_UPLOAD_MIME_TYPES,
  MAX_BASE_TEXT_LENGTH,
  accountWord,
  charCount,
} from './social-platforms';
import { timeZoneName, whenWords } from './social-ui';
import { socialWords as w } from './social-words';

/** An account's name in the card: the platform's word where the organization has one account on
 *  that platform, and the account's own name beside it where it has more. */
export function accountLabel(channel: Channel, all: readonly Channel[]): string {
  const word = accountWord(channel.platform);
  const several = all.filter((other) => other.platform === channel.platform).length > 1;
  return several ? `${word} · ${channel.name}` : word;
}

/** A link as a line reads it: no scheme, no trailing slash. */
function shortLink(link: string): string {
  return link.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

export function ComposerCard(props: ComposerProps) {
  const c = useComposer(props);
  const {
    config,
    post,
    entry,
    host,
    state,
    set,
    views,
    blocks,
    reason,
    taggedContext,
    saveDraft,
    publish,
    act,
    busy,
    failure,
    current,
    deleting,
    setDeleting,
    onDeleted,
    orgRef,
    postId,
    zone,
  } = c;
  const schedule = useScheduleFields(entry.suggestedAt, zone);
  const [previewing, setPreviewing] = useState(false);
  const link = state.link.trim();
  const instagramChosen = views.some((view) => view.channel.platform === 'instagram');
  const hasContent = state.text.trim().length > 0 || state.channels.length > 0;
  const scheduleIt = () =>
    act(() => {
      const when = schedule.check();
      if (when !== null) publish.mutate(when);
    });
  const sending = publish.isPending ? publish.variables : undefined;

  return (
    <>
      <Card className="flex flex-col gap-0 p-0" testId="social-composer">
        <div className="border-b border-border-subtle px-20 py-16">
          <h2 className="font-sans text-s16 font-w600 tracking-givmo text-text-strong">
            {post ? w.editDraftTitle : w.composeTitle}
          </h2>
        </div>
        <div className="flex flex-col gap-20 px-20 py-20">
          {taggedContext ? (
            <div
              className="flex items-center justify-between gap-12 border-b border-border-subtle pb-16"
              {...testIdProp('social-context')}
            >
              <span className="font-sans text-s14 font-w500 tracking-givmo text-text-strong">
                {w.campaignTagged(taggedContext.label ?? w.campaignUnnamed)}
              </span>
              <Button
                appearance="saas"
                tone="link"
                size="sm"
                onClick={() => set({ context: null })}
                testId="social-context-remove"
              >
                {w.removeCampaign}
              </Button>
            </div>
          ) : null}

          <GraphicPicker c={c} />

          <div className="flex flex-col gap-6">
            <Textarea
              label={w.captionLabel}
              value={state.text}
              onChange={(event) => set({ text: event.target.value })}
              placeholder={w.textPlaceholder}
              rows={3}
              error={blocks.includes('text_too_long')}
              errorMessage={blocks.includes('text_too_long') ? w.baseTooLong : undefined}
              testId="social-text"
            />
            <span
              className={cn(
                'self-end font-sans text-s12 tabular-nums tracking-givmo',
                charCount(state.text) > MAX_BASE_TEXT_LENGTH
                  ? 'text-danger-700'
                  : 'text-text-secondary',
              )}
              {...testIdProp('social-text-count')}
            >
              {charCount(state.text).toLocaleString()} / {MAX_BASE_TEXT_LENGTH.toLocaleString()}
            </span>
          </div>

          {link ? (
            <div className="-mt-8 flex flex-col gap-8">
              <div
                className="flex min-w-0 items-center gap-8 rounded-md bg-surface-sunken px-14 py-12"
                {...testIdProp('social-campaign-link')}
              >
                <Icon icon={Link2} size={16} aria-hidden className="shrink-0 text-text-secondary" />
                <span className="shrink-0 font-sans text-s14 tracking-givmo text-text-secondary">
                  {state.context ? w.linksToCampaign : w.linksTo}
                </span>{' '}
                <span className="min-w-0 truncate font-sans text-s14 font-w500 tracking-givmo text-text-strong">
                  {shortLink(link)}
                </span>
              </div>
              {instagramChosen ? (
                <p
                  className="font-sans text-s13 tracking-givmo text-text-secondary"
                  {...testIdProp('social-instagram-bio')}
                >
                  {w.instagramBio(config.orgName ?? null)}
                </p>
              ) : null}
            </div>
          ) : null}

          <PostTo c={c} />

          <section className="flex flex-col gap-12">
            <button
              type="button"
              aria-expanded={previewing}
              onClick={() => setPreviewing((open) => !open)}
              className="inline-flex w-fit items-center gap-6 rounded-sm font-sans text-s14 font-w500 tracking-givmo text-brand-600 hover:text-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              {...testIdProp('social-preview-toggle')}
            >
              <Icon icon={previewing ? ChevronDown : ChevronRight} size={16} aria-hidden />
              {w.previewToggle}
            </button>
            {previewing ? <PreviewCards c={c} /> : null}
          </section>

          <section className="flex flex-col gap-8" aria-label={w.scheduleTitle}>
            <div className="flex flex-wrap items-end gap-12">
              <div className="w-[200px] max-w-full">
                <Field
                  label={w.scheduleDate}
                  type="date"
                  value={schedule.day}
                  onChange={(event) => schedule.setDay(event.target.value)}
                  testId="social-schedule-date"
                />
              </div>
              <div className="w-[160px] max-w-full">
                <Field
                  label={w.scheduleTime}
                  type="time"
                  value={schedule.time}
                  onChange={(event) => schedule.setTime(event.target.value)}
                  testId="social-schedule-time"
                />
              </div>
              {/* The chosen time and zone right above Schedule, where the person clicks: under the
                  date fields it was missed while writing (C4). */}
              <div className="ml-auto flex min-w-0 flex-col items-end gap-6">
                {schedule.chosen ? (
                  <p
                    className="max-w-[300px] text-right font-sans text-s13 font-w600 tracking-givmo text-text-strong"
                    {...testIdProp('social-schedule-readback')}
                  >
                    {state.channels.length > 0
                      ? w.scheduleReadBack(
                          whenWords(schedule.chosen.toISOString(), zone),
                          timeZoneName(zone),
                          state.channels.length,
                        )
                      : w.scheduleAtWords(
                          whenWords(schedule.chosen.toISOString(), zone),
                          timeZoneName(zone),
                        )}
                  </p>
                ) : null}
                <Button
                  appearance="saas"
                  tone="primary"
                  loading={sending instanceof Date}
                  disabled={busy || reason !== null}
                  onClick={scheduleIt}
                  testId="social-schedule"
                >
                  {w.schedule}
                </Button>
              </div>
            </div>
            {schedule.problem ? (
              <ErrorMessage testId="social-schedule-error">{schedule.problem}</ErrorMessage>
            ) : null}
          </section>
        </div>

        <div
          className="flex flex-wrap items-center justify-end gap-8 border-t border-border-subtle px-20 py-14"
          {...testIdProp('social-composer-actions')}
        >
          {failure ? (
            <ErrorMessage className="mr-auto" testId="social-composer-error">
              {actionErrorWords(failure)}
            </ErrorMessage>
          ) : reason !== null && hasContent ? (
            <span
              className="mr-auto font-sans text-s13 font-w500 tracking-givmo text-warning-700"
              role="status"
              {...testIdProp('social-composer-reason')}
            >
              {reason}
            </span>
          ) : null}
          {current !== null && current.deletable && onDeleted ? (
            <Button
              appearance="saas"
              tone="ghost"
              disabled={busy}
              onClick={() => setDeleting(true)}
              testId="social-delete-draft"
            >
              {w.deleteDraft}
            </Button>
          ) : null}
          {host.onCancel ? (
            <Button
              appearance="saas"
              tone="ghost"
              disabled={busy}
              onClick={host.onCancel}
              testId="social-composer-cancel"
            >
              {w.cancel}
            </Button>
          ) : null}
          <Button
            appearance="saas"
            tone="secondary"
            loading={saveDraft.isPending}
            disabled={busy || reason !== null}
            onClick={() => act(() => saveDraft.mutate())}
            testId="social-save-draft"
          >
            {w.saveDraft}
          </Button>
          <Button
            appearance="saas"
            tone="secondary"
            loading={sending === null}
            disabled={busy || reason !== null}
            onClick={() => act(() => publish.mutate(null))}
            testId="social-publish-now"
          >
            {w.publishNow}
          </Button>
        </div>
      </Card>
      <UntickDialog c={c} />
      {deleting && orgRef !== null && postId !== null && onDeleted ? (
        <DeleteDraftDialog
          orgRef={orgRef}
          postId={postId}
          onClose={() => setDeleting(false)}
          onDeleted={() => onDeleted(postId)}
        />
      ) : null}
    </>
  );
}

/** Frame 40's Graphic row: the image the post carries (when it isn't one of the offered ones), the
 *  campaign's Collateral graphics, and a tile to add an image from the computer. */
function GraphicPicker({ c }: { c: ComposerController }) {
  const { state, graphics, graphicId, graphic, imageBusy, imageError, pickFile, removeImage } = c;
  const fileInput = useRef<HTMLInputElement>(null);
  const offeredChosen =
    state.image !== null && graphicId !== null && graphics.some((item) => item.id === graphicId);
  const attached = state.image !== null && !offeredChosen ? state.image : null;
  const busyId = graphic.isPending ? (graphic.variables?.id ?? null) : null;
  return (
    <section className="flex flex-col gap-6" aria-labelledby="social-graphic-label">
      <h3
        id="social-graphic-label"
        className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary"
      >
        {w.graphicLabel}
      </h3>
      <input
        ref={fileInput}
        type="file"
        accept={IMAGE_UPLOAD_MIME_TYPES.join(',')}
        className="hidden"
        onChange={(event) => {
          pickFile(event.target.files?.[0]);
          event.target.value = '';
        }}
        {...testIdProp('social-image-input')}
      />
      <div className="flex flex-wrap gap-10" {...testIdProp('social-collateral')}>
        {attached ? (
          <GraphicTile
            src={attached.url}
            label={w.attachedImage}
            chosen
            onRemove={removeImage}
            testId="social-image-preview"
          />
        ) : null}
        {graphics.slice(0, 8).map((item) => (
          <GraphicTile
            key={item.id}
            src={item.url}
            label={`${w.useGraphic}: ${item.title}`}
            chosen={offeredChosen && graphicId === item.id}
            busy={busyId === item.id}
            disabled={imageBusy}
            onPick={() => graphic.mutate(item)}
            onRemove={removeImage}
            testId={`social-collateral-${item.id}`}
          />
        ))}
        <button
          type="button"
          aria-label={w.addAnImage}
          disabled={imageBusy}
          onClick={() => fileInput.current?.click()}
          className={cn(
            'inline-flex size-[80px] items-center justify-center rounded-md border border-dashed border-border-strong text-text-secondary transition-colors',
            'hover:border-brand-600 hover:text-brand-600',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600',
            imageBusy && 'cursor-progress opacity-60',
          )}
          {...testIdProp('social-image-add')}
        >
          <Icon icon={ImagePlus} size={22} aria-hidden />
        </button>
      </div>
      {imageError ? <ErrorMessage testId="social-image-error">{imageError}</ErrorMessage> : null}
    </section>
  );
}

function GraphicTile({
  src,
  label,
  chosen,
  busy = false,
  disabled = false,
  onPick,
  onRemove,
  testId,
}: {
  src: string;
  label: string;
  chosen: boolean;
  busy?: boolean;
  disabled?: boolean;
  onPick?: () => void;
  onRemove: () => void;
  testId: string;
}) {
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label={label}
        aria-pressed={chosen}
        disabled={disabled && !chosen}
        onClick={chosen ? undefined : onPick}
        className={cn(
          'size-[80px] overflow-hidden rounded-md border border-border-subtle bg-surface-sunken',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600',
          chosen && 'outline-2 outline-offset-2 outline-brand-600 outline-solid',
          busy && 'opacity-60',
        )}
        {...testIdProp(testId)}
      >
        <img src={src} alt="" className="size-full object-cover" />
      </button>
      {chosen ? (
        <button
          type="button"
          aria-label={w.removeTheImage}
          onClick={onRemove}
          className="absolute -top-8 -right-8 inline-flex size-[22px] items-center justify-center rounded-full border border-border-subtle bg-surface text-text-secondary shadow-sm hover:text-text-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          {...testIdProp('social-image-remove')}
        >
          <Icon icon={X} size={12} aria-hidden />
        </button>
      ) : null}
    </span>
  );
}

/** Frame 40's "Post to": a checkbox per connected account, its mark and its name; the reconnect
 *  line under them. */
function PostTo({ c }: { c: ComposerController }) {
  const { connections, channels, lapsedChannels, allChannels, state, toggleChannel, config } = c;
  return (
    <section className="flex flex-col gap-8" aria-labelledby="social-post-to">
      <h3
        id="social-post-to"
        className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary"
      >
        {w.postTo}
      </h3>
      {connections.isPending ? null : channels.length === 0 && lapsedChannels.length === 0 ? (
        <NoChannels manager={canManageConnections(config)} />
      ) : (
        <div className="flex flex-wrap gap-x-24 gap-y-10" {...testIdProp('social-channels')}>
          {channels.map((channel) => {
            const nameId = `social-channel-name-${channel.connectionId}`;
            return (
              <label
                key={channel.connectionId}
                className="inline-flex cursor-pointer items-center gap-8 font-sans text-s14 tracking-givmo text-text-strong"
              >
                <Checkbox
                  appearance="saas"
                  checked={state.channels.includes(channel.connectionId)}
                  onCheckedChange={() => toggleChannel(channel.connectionId)}
                  aria-labelledby={nameId}
                  testId={`social-channel-${channel.connectionId}`}
                />
                <PlatformMark platform={channel.platform} size={22} />
                <span id={nameId} className="max-w-[260px] truncate">
                  {accountLabel(channel, allChannels)}
                </span>
              </label>
            );
          })}
        </div>
      )}
      <LapsedChannels c={c} nameOf={(channel) => accountLabel(channel, allChannels)} />
    </section>
  );
}
